import { runsScaleFontSize } from "./textRuns";
import type { SceneObject } from "./SceneHost";
import {
  applyPathGeometry,
  isPathObject,
  scalePathPointsInGroup,
  syncPathElement,
  type PathPoint,
} from "./pathGeometry";
import { syncSegmentOnGroup } from "./render/renderers/segment";
import { isSegmentObject } from "./segmentGeometry";
import { isShapeObject, syncShapeOnGroup } from "./render/shapes/syncShape";
import {
  clampTextLikePatchToContent,
  isTextLikeObject,
  isTextObject,
  mergeTextMeta,
  scaleTextPatchUniform,
  textWidthMode,
} from "./textObject";
import { syncTextLikeOnGroup } from "./render/renderers/textObject";
import { isFrameObject } from "./frame/frameContext";
import { syncFrameOnGroup } from "./render/renderers/frame";
import { objectBounds, unionBounds, type Bounds } from "./selectionBounds";
import { applyObjectBounds } from "./sceneObjectDom";
import { sceneLayerSvgFor, syncSceneLayerSurface } from "./render/SvgRenderer";
import {
  SmartGuideOverlay,
  snapResizeBounds,
  snapThresholdWorld,
  SMART_GUIDE_SNAP_PX,
  isAlignSnapTarget,
  visibleWorldBoundsFromViewport,
} from "./smartGuides";
import {
  boundsFromResizeHandle,
  boundsScaledFromHandle,
  captureObjectSnapshot,
  fontScaleFromGroupResize,
  patchFromObject,
  scaleSnapshots,
  scaledFontSize,
  snapshotToPatch,
  cornerScaleFromFreeBounds,
  type ObjectTransformSnapshot,
  type ResizeHandleId,
} from "./transform/resize";
import {
  applyGroupRotationDelta,
  objectRotationRad,
  pointerAngleRad,
  rotatePoint,
  ROTATE_HANDLE_OFFSET,
  selectionShowsRotateHandle,
  usesRotationField,
} from "./transform/rotate";

const HANDLES: ResizeHandleId[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

/** Шаг привязки поворота при зажатом Shift. */
const ROTATE_SNAP_RAD = (15 * Math.PI) / 180;
/** Магнит к 0/90/180/270: столько градусов «дотягиваем» до прямого угла. */
const ROTATE_RIGHT_ANGLE_MAGNET_RAD = (1.5 * Math.PI) / 180;

/**
 * Угол рамки. Рамка поворачивается вместе с объектом только когда объект один:
 * у группы поворот у каждого свой, и общая рамка остаётся по осям.
 */
function selectionRotationRad(objects: SceneObject[]): number {
  if (objects.length !== 1) return 0;
  const obj = objects[0]!;
  if (!usesRotationField(obj)) return 0;
  const rot = objectRotationRad(obj);
  return Math.abs(rot) < 1e-6 ? 0 : rot;
}

/**
 * Точка, которая при растягивании должна остаться на месте: противоположный
 * угол или середина противоположной стороны. С Alt тянем от центра.
 */
function resizeAnchorPoint(b: Bounds, handle: ResizeHandleId, altKey: boolean) {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  if (altKey) return { x: cx, y: cy };
  const x = handle.includes("w") ? b.x + b.w : handle.includes("e") ? b.x : cx;
  const y = handle.includes("n") ? b.y + b.h : handle.includes("s") ? b.y : cy;
  return { x, y };
}

type FrameHost = {
  canEdit: boolean;
  getActiveTool(): string;
  getSelection(): string[];
  getObjects(): SceneObject[];
  findObject(id: string): SceneObject | null;
  getObjectElement(id: string): Element | null;
  stagePoint(clientX: number, clientY: number): { x: number; y: number };
  getSvgDefs?(): SVGDefsElement | undefined;
  commitBatchPatches(
    patches: { id: string; patch: Record<string, unknown> }[],
    clientOpId: string,
    undoEntry?: {
      forward: { type: "update_object"; id: string; patch: Record<string, unknown> }[];
      inverse: { type: "update_object"; id: string; patch: Record<string, unknown> }[];
    }
  ): Promise<void>;
  onLayoutChange?: () => void;
  isTextEditing?: () => boolean;
};

type ResizeState = {
  handle: ResizeHandleId;
  startBounds: Bounds;
  candidateBounds: Bounds[];
  guides: SmartGuideOverlay;
  snapshots: Map<string, Bounds>;
  transformSnapshots: Map<string, ObjectTransformSnapshot>;
  pathPointSnapshots: Map<string, PathPoint[]>;
  /** Текст за угол — как в Miro: пропорции, масштаб шрифта. */
  /** Драг за угол: масштаб пропорциональный (Shift временно отпускает пропорции). */
  lockCornerAspect: boolean;
  /** Угол одиночного повёрнутого объекта: тянем вдоль его собственных сторон. */
  rotationRad: number;
};

type RotateState = {
  groupCenter: { x: number; y: number };
  startAngle: number;
  /** Угол объекта до начала поворота — от него считаются привязки и подпись. */
  baseRotation: number;
  snapshots: Map<string, ObjectTransformSnapshot>;
  moved: boolean;
};

/**
 * Привязки поворота. Shift — шаг 15°, иначе мягкий магнит к прямым углам:
 * без него вернуть объект ровно в 0° или 90° мышью практически нельзя.
 */
function snapRotationDelta(delta: number, baseRotation: number, shiftKey: boolean): number {
  const total = baseRotation + delta;
  if (shiftKey) {
    return Math.round(total / ROTATE_SNAP_RAD) * ROTATE_SNAP_RAD - baseRotation;
  }
  const quarter = Math.PI / 2;
  const nearest = Math.round(total / quarter) * quarter;
  if (Math.abs(total - nearest) <= ROTATE_RIGHT_ANGLE_MAGNET_RAD) {
    return nearest - baseRotation;
  }
  return delta;
}

/** Градусы в диапазоне (-180, 180] — подпись не должна показывать 359°. */
function formatAngleDeg(rad: number): string {
  let deg = (rad * 180) / Math.PI;
  deg = ((deg % 360) + 360) % 360;
  if (deg > 180) deg -= 360;
  return `${Math.round(deg)}°`;
}

export type SelectionFrameController = {
  update: () => void;
  destroy: () => void;
};

function applyBoundsToEl(el: Element, b: Bounds) {
  applyObjectBounds(el, b);
}

/**
 * Виджеты с собственной «начинкой» (плеер, кнопки таймера/калькулятора,
 * редактор кода, навигация PDF). Рамка над ними должна быть только визуальной,
 * иначе выделенный виджет перестаёт реагировать на свои же контролы.
 */
const WIDGET_CHROME_TYPES = new Set([
  "audio",
  "timer",
  "calculator",
  "code",
  "sql",
  "pdf_document",
]);

function isWidgetChromeObject(obj: SceneObject): boolean {
  return typeof obj.type === "string" && WIDGET_CHROME_TYPES.has(obj.type);
}

function syncObjectDom(host: FrameHost, obj: SceneObject, el: Element) {
  if (isPathObject(obj)) {
    syncPathElement(el, obj);
    return;
  }
  if (isShapeObject(obj) && el instanceof SVGGElement) {
    syncShapeOnGroup(el, obj);
    return;
  }
  if (isTextLikeObject(obj) && el instanceof SVGGElement) {
    syncTextLikeOnGroup(el, obj, Boolean(host.isTextEditing?.()));
    return;
  }
  if (isFrameObject(obj) && el instanceof SVGGElement) {
    syncFrameOnGroup(el, obj);
    return;
  }
  if (isSegmentObject(obj) && el instanceof SVGGElement) {
    syncSegmentOnGroup(el, obj, {
      defs: host.getSvgDefs?.(),
      markerEnd: obj.type === "arrow",
    });
    return;
  }
  applyObjectBounds(el, objectBounds(obj));
}

export function createSelectionFrame(
  overlay: HTMLElement,
  host: FrameHost
): SelectionFrameController {
  const root = document.createElement("div");
  root.className = "wb-selection-frame wb-hidden";
  root.setAttribute("aria-hidden", "true");

  const box = document.createElement("div");
  box.className = "wb-selection-frame__box";
  root.appendChild(box);

  const handleEls = new Map<ResizeHandleId, HTMLElement>();
  for (const h of HANDLES) {
    const el = document.createElement("div");
    el.className = `wb-selection-handle wb-selection-handle--${h}`;
    el.dataset.handle = h;
    el.title =
      h === "n" || h === "s"
        ? "Высота (Shift — пропорции, Alt — от центра)"
        : h === "e" || h === "w"
          ? "Ширина (перенос строк, как в Miro)"
          : "Размер шрифта (угол, как в Miro)";
    root.appendChild(el);
    handleEls.set(h, el);
  }

  const rotateHandle = document.createElement("div");
  rotateHandle.className = "wb-selection-handle wb-selection-handle--rotate";
  rotateHandle.dataset.handle = "rotate";
  rotateHandle.title = "Поворот";
  root.appendChild(rotateHandle);

  const rotateStem = document.createElement("div");
  rotateStem.className = "wb-selection-frame__rotate-stem";
  root.appendChild(rotateStem);

  // Подпись с углом: во время поворота видно, на сколько градусов повернули.
  const rotateReadout = document.createElement("div");
  rotateReadout.className = "wb-selection-frame__angle";
  rotateReadout.style.display = "none";
  root.appendChild(rotateReadout);

  overlay.appendChild(root);

  let resize: ResizeState | null = null;
  let rotate: RotateState | null = null;
  /** Сколько единиц доски в одном пикселе экрана (обновляется в syncChromeScale). */
  let chromeScale = 1;
  /** Последняя расстановка рамки — чтобы пересчитать её при зуме. */
  let lastLayout: { b: Bounds; showRotate: boolean } | null = null;

  function positionHandles(b: Bounds, showRotate: boolean) {
    syncChromeScale();
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const positions: Record<ResizeHandleId, [number, number]> = {
      nw: [b.x, b.y],
      n: [cx, b.y],
      ne: [b.x + b.w, b.y],
      e: [b.x + b.w, cy],
      se: [b.x + b.w, b.y + b.h],
      s: [cx, b.y + b.h],
      sw: [b.x, b.y + b.h],
      w: [b.x, cy],
    };
    handleEls.forEach((el, id) => {
      const [hx, hy] = positions[id];
      el.style.left = `${hx}px`;
      el.style.top = `${hy}px`;
    });
    // Вынос ручки поворота — в пикселях экрана, а не доски: иначе на мелком
    // масштабе она налезала на ручку размера и вместо поворота хватался
    // ресайз, а на крупном уезжала далеко от объекта.
    //
    // Ручка стоит СНИЗУ. Сверху её загораживала панель свойств выделенного
    // объекта — она всплывает над выделением, и ручку поворота было просто
    // не найти. Снизу над объектом ничего не висит.
    const offset = ROTATE_HANDLE_OFFSET * chromeScale;
    const bottom = b.y + b.h;
    rotateHandle.style.left = `${cx}px`;
    rotateHandle.style.top = `${bottom + offset}px`;
    rotateStem.style.display = showRotate ? "" : "none";
    if (showRotate) {
      const stemHeight = Math.max(0, offset - 7 * chromeScale);
      rotateStem.style.left = `${cx}px`;
      rotateStem.style.top = `${bottom}px`;
      rotateStem.style.height = `${stemHeight}px`;
    }
    rotateReadout.style.left = `${cx}px`;
    rotateReadout.style.top = `${bottom + offset + 14 * chromeScale}px`;
  }

  /** Ручки/обводка в world-слое: компенсируем zoom → на экране постоянный размер. */
  function syncChromeScale() {
    const a = host.stagePoint(0, 0);
    const b = host.stagePoint(1, 0);
    const worldPerPx = Math.abs(b.x - a.x);
    const s = Number.isFinite(worldPerPx) && worldPerPx > 0 ? worldPerPx : 1;
    chromeScale = s;
    root.style.setProperty("--wb-sel-chrome-scale", String(s));
  }

  function onLiveViewport() {
    if (root.classList.contains("wb-hidden")) return;
    syncChromeScale();
    // Пересчитываем и расстановку: вынос ручки поворота считается в пикселях
    // экрана, и без этого при зуме она отъезжала от рамки.
    if (lastLayout) positionHandles(lastLayout.b, lastLayout.showRotate);
  }

  function setTransforming(on: boolean) {
    const viewportEl =
      overlay.closest(".wb-board-viewport") ||
      overlay.parentElement?.closest(".wb-board-viewport");
    if (viewportEl instanceof HTMLElement) {
      viewportEl.classList.toggle("is-selection-dragging", on);
    }
    window.dispatchEvent(new CustomEvent("wb:selection-drag", { detail: on }));
  }

  function isTransformingChrome() {
    const viewportEl =
      overlay.closest(".wb-board-viewport") ||
      overlay.parentElement?.closest(".wb-board-viewport");
    return (
      viewportEl instanceof HTMLElement &&
      viewportEl.classList.contains("is-selection-dragging")
    );
  }

  function applyGroupLayout(b: Bounds, showRotate: boolean, rotationRad = 0) {
    lastLayout = { b, showRotate };
    applyBoundsToEl(box, b);
    positionHandles(b, showRotate);
    rotateHandle.style.display = showRotate ? "" : "none";
    // Рамку поворачиваем целиком вместе с объектом — ручки остаются на его
    // настоящих углах, а не на углах описанного прямоугольника.
    if (Math.abs(rotationRad) > 1e-6) {
      root.style.transformOrigin = `${b.x + b.w / 2}px ${b.y + b.h / 2}px`;
      root.style.transform = `rotate(${(rotationRad * 180) / Math.PI}deg)`;
    } else {
      root.style.transform = "";
      root.style.transformOrigin = "";
    }
    if (!isTransformingChrome()) {
      host.onLayoutChange?.();
    }
  }

  function applyPatchesToDom(
    patches: Map<string, { x: number; y: number; w: number; h: number }>,
    groupStart: Bounds,
    groupNext: Bounds,
    pathPointSnapshots: Map<string, PathPoint[]>,
    transformSnapshots: Map<string, ObjectTransformSnapshot>,
    handle: ResizeHandleId,
    altKey: boolean,
    objectSnapshots: Map<string, Bounds>
  ) {
    const fontScale = fontScaleFromGroupResize(groupStart, groupNext);
    const sideHandle = handle === "e" || handle === "w";
    const cornerHandle = handle.length === 2;
    patches.forEach((patch, id) => {
      const obj = host.findObject(id);
      const el = host.getObjectElement(id);
      if (!obj || !el) return;
      if (isPathObject(obj)) {
        const originPts = pathPointSnapshots.get(id);
        if (originPts) {
          const scaled = scalePathPointsInGroup(originPts, groupStart, groupNext);
          applyPathGeometry(obj, scaled);
          syncPathElement(el, obj);
        }
        return;
      }
      let nextPatch = patch;
      obj.x = nextPatch.x;
      obj.y = nextPatch.y;
      obj.w = nextPatch.w;
      obj.h = nextPatch.h;
      if (isTextLikeObject(obj)) {
        const origin = transformSnapshots.get(id);
        const startFs =
          typeof origin?.fontSize === "number" && Number.isFinite(origin.fontSize)
            ? origin.fontSize
            : undefined;
        const objStart = objectSnapshots.get(id);
        /** Куски со своим размером — за общим размером надписи, в той же пропорции. */
        const syncRunsToFont = () => {
          if (!origin?.runs || typeof startFs !== "number" || startFs <= 0) return;
          const fs = Number((obj.style as { fontSize?: unknown } | undefined)?.fontSize);
          if (!Number.isFinite(fs) || fs <= 0) return;
          obj.runs = runsScaleFontSize(origin.runs, fs / startFs);
        };
        const textSide = isTextObject(obj) && sideHandle;
        const textCorner = isTextObject(obj) && cornerHandle;

        if (textCorner && typeof startFs === "number" && objStart) {
          // Miro: угол — пропорционально меняет кегль, строки не перетекают.
          const sx = objStart.w > 0 ? patch.w / objStart.w : 1;
          const scaled = scaleTextPatchUniform(objStart, handle, sx, startFs, altKey);
          obj.style = {
            ...(obj.style ?? {}),
            fontSize: scaled.fontSize,
          };
          syncRunsToFont();
          obj.x = scaled.x;
          obj.y = scaled.y;
          obj.w = scaled.w;
          obj.h = scaled.h;
        } else if (textSide && typeof startFs === "number" && objStart) {
          // Miro: бок — ширина + перенос, кегль тот же; дальше ширина «fixed».
          obj.style = {
            ...(obj.style ?? {}),
            fontSize: startFs,
          };
          syncRunsToFont();
          obj.meta = mergeTextMeta(obj, { textWidthMode: "fixed", textWidthGrow: false });
          nextPatch = clampTextLikePatchToContent(
            { x: obj.x, y: obj.y, w: obj.w, h: obj.h },
            obj,
            handle,
            objStart,
            altKey
          );
          obj.x = nextPatch.x;
          obj.y = nextPatch.y;
          obj.w = nextPatch.w;
          obj.h = nextPatch.h;
        } else {
          if (typeof startFs === "number") {
            obj.style = {
              ...(obj.style ?? {}),
              fontSize: scaledFontSize(startFs, fontScale),
            };
            syncRunsToFont();
          }
          if (objStart) {
            nextPatch = clampTextLikePatchToContent(
              { x: obj.x, y: obj.y, w: obj.w, h: obj.h },
              obj,
              handle,
              objStart,
              altKey
            );
            obj.x = nextPatch.x;
            obj.y = nextPatch.y;
            obj.w = nextPatch.w;
            obj.h = nextPatch.h;
            if (typeof startFs === "number") {
              const sx = objStart.w > 0 ? obj.w / objStart.w : 1;
              const sy = objStart.h > 0 ? obj.h / objStart.h : 1;
              const appliedScale = Math.sqrt(Math.max(1e-6, sx) * Math.max(1e-6, sy));
              obj.style = {
                ...(obj.style ?? {}),
                fontSize: scaledFontSize(startFs, appliedScale),
              };
              syncRunsToFont();
              nextPatch = clampTextLikePatchToContent(
                { x: obj.x, y: obj.y, w: obj.w, h: obj.h },
                obj,
                handle,
                objStart,
                altKey
              );
              obj.x = nextPatch.x;
              obj.y = nextPatch.y;
              obj.w = nextPatch.w;
              obj.h = nextPatch.h;
            }
          }
        }
      }
      syncObjectDom(host, obj, el);
    });
    // Узел объекта с вёрсткой — не SVG, но слой у него тот же.
    const firstEl = host.getObjectElement(host.getSelection()[0] ?? "");
    const svg = sceneLayerSvgFor(firstEl);
    if (svg) syncSceneLayerSurface(svg, host.getObjects());
  }

  function commitTransformPatches(
    snapshots: Map<string, ObjectTransformSnapshot>,
    clientOpId: string
  ) {
    const ids = host.getSelection();
    const patches: { id: string; patch: Record<string, unknown> }[] = [];
    const forward: { type: "update_object"; id: string; patch: Record<string, unknown> }[] = [];
    const inverse: { type: "update_object"; id: string; patch: Record<string, unknown> }[] = [];

    for (const id of ids) {
      const obj = host.findObject(id);
      const origin = snapshots.get(id);
      if (!obj || !origin) continue;
      const patch = patchFromObject(obj);
      patches.push({ id, patch });
      forward.push({ type: "update_object", id, patch });
      inverse.push({ type: "update_object", id, patch: snapshotToPatch(origin) });
    }

    if (!patches.length) return;
    void host.commitBatchPatches(patches, clientOpId, { forward, inverse: inverse.reverse() });
  }

  function update() {
    if (!host.canEdit || host.getActiveTool() !== "select") {
      root.classList.add("wb-hidden");
      host.onLayoutChange?.();
      return;
    }
    // Рамку с ручками показываем и во время набора: размер надписи должен
    // меняться сразу, не выходя из текста. Раньше ручки были только у надписи
    // с зафиксированной шириной, а новая создаётся «растущей» — и поменять
    // размер было нечем.
    if (typeof host.isTextEditing === "function" && host.isTextEditing()) {
      const editingIds = host.getSelection();
      const editingSet = new Set(editingIds);
      const editingObjs = host.getObjects().filter((o) => editingSet.has(o.id));
      const singleTextEdit = editingObjs.length === 1 && isTextObject(editingObjs[0]!);
      if (!singleTextEdit) {
        root.classList.add("wb-hidden");
        host.onLayoutChange?.();
        return;
      }
    }
    const ids = host.getSelection();
    if (!ids.length) {
      root.classList.add("wb-hidden");
      root.classList.remove("wb-selection-frame--segment-only");
      root.classList.remove("wb-selection-frame--stroke-chrome");
      root.classList.remove("wb-selection-frame--widget-chrome");
      host.onLayoutChange?.();
      return;
    }
    // Set, а не ids.includes: update() зовётся каждый кадр перетаскивания,
    // и линейный поиск внутри filter давал квадрат — на большом выделении
    // это подвешивало вкладку вместе с рамкой и панелью над объектами.
    const idSet = new Set(ids);
    const objects = host.getObjects().filter((o) => idSet.has(o.id));
    const bounds = unionBounds(objects);
    if (!bounds || bounds.w < 1 || bounds.h < 1) {
      root.classList.add("wb-hidden");
      return;
    }
    const allLocked = objects.every((o) => o.locked);
    root.classList.remove("wb-hidden");
    root.classList.toggle("wb-selection-frame--locked", allLocked);
    const segmentOnly = objects.length === 1 && isSegmentObject(objects[0]!);
    // Перо: рамка только визуальная — тянуть/выделять по штриху, не по AABB.
    const strokeChrome = objects.length === 1 && isPathObject(objects[0]!);
    // Текст auto: высота по содержимому — вертикальные ручки лишние; fixed — все ручки.
    const textOnly = objects.length === 1 && isTextObject(objects[0]!);
    const textFixed = textOnly && textWidthMode(objects[0]!) === "fixed";
    handleEls.forEach((el, id) => {
      if (id === "n" || id === "s") {
        el.style.display = textOnly && !textFixed ? "none" : "";
      }
    });
    // Виджет в одиночном выделении: рамку рисуем, но клики пропускаем внутрь —
    // иначе плеер/кнопки/редактор кода мертвы, пока объект выделен.
    const widgetChrome = objects.length === 1 && isWidgetChromeObject(objects[0]!);
    root.classList.toggle("wb-selection-frame--segment-only", segmentOnly);
    root.classList.toggle("wb-selection-frame--stroke-chrome", strokeChrome);
    root.classList.toggle("wb-selection-frame--widget-chrome", widgetChrome);
    applyGroupLayout(bounds, selectionShowsRotateHandle(objects), selectionRotationRad(objects));
  }

  function onHandleDown(ev: PointerEvent) {
    if (!host.canEdit) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    const handle = (ev.currentTarget as HTMLElement).dataset.handle as ResizeHandleId | undefined;
    if (!handle) return;
    ev.preventDefault();
    ev.stopPropagation();

    const ids = host.getSelection().filter((id) => {
      const obj = host.findObject(id);
      return obj && !obj.locked;
    });
    if (!ids.length) return;
    const snapshots = new Map<string, Bounds>();
    const transformSnapshots = new Map<string, ObjectTransformSnapshot>();
    const pathPointSnapshots = new Map<string, PathPoint[]>();
    const selectedIdSet = new Set(ids);
    const candidateBounds = host
      .getObjects()
      .filter((obj) => !selectedIdSet.has(obj.id) && isAlignSnapTarget(obj))
      .map((obj) => objectBounds(obj));
    for (const id of ids) {
      const obj = host.findObject(id);
      if (!obj) continue;
      snapshots.set(id, objectBounds(obj));
      transformSnapshots.set(id, captureObjectSnapshot(obj));
      if (isPathObject(obj)) {
        pathPointSnapshots.set(id, obj.points.map((p) => [p[0], p[1]] as PathPoint));
      }
    }
    const startBounds = unionBounds(
      ids.map((id) => host.findObject(id)).filter(Boolean) as SceneObject[]
    );
    if (!startBounds) return;
    const guides = new SmartGuideOverlay(overlay);
    // За угол тянем пропорционально любой объект: раньше так вёл себя только
    // текст, а фигуры и картинки перекашивались, если рука уходила вбок.
    const lockCornerAspect = handle.length === 2;

    resize = {
      handle,
      startBounds,
      candidateBounds,
      guides,
      snapshots,
      transformSnapshots,
      pathPointSnapshots,
      lockCornerAspect,
      rotationRad: selectionRotationRad(
        ids.map((id) => host.findObject(id)).filter(Boolean) as SceneObject[]
      ),
    };

    setTransforming(true);

    document.addEventListener("pointermove", onResizeMove);
    document.addEventListener("pointerup", onResizeUp);
    document.addEventListener("pointercancel", onResizeCancel);
  }

  function onResizeMove(ev: PointerEvent) {
    if (!resize) return;
    const raw = host.stagePoint(ev.clientX, ev.clientY);
    const startCx = resize.startBounds.x + resize.startBounds.w / 2;
    const startCy = resize.startBounds.y + resize.startBounds.h / 2;
    // У повёрнутого объекта тянем в его собственных осях: указатель
    // «разворачиваем» обратно, считаем как обычно, а потом правим смещение.
    const pt = resize.rotationRad
      ? (() => {
          const [lx, ly] = rotatePoint(raw.x, raw.y, startCx, startCy, -resize.rotationRad);
          return { x: lx, y: ly };
        })()
      : raw;
    // За угол: сначала свободный драг, затем единый масштаб по доминирующей оси.
    // Если включить aspect-lock прямо в boundsFromResizeHandle, объект сжимается
    // при любом движении не строго по диагонали.
    const keepAspect = resize.lockCornerAspect && !ev.shiftKey;
    const freeGroup = boundsFromResizeHandle(
      resize.handle,
      resize.startBounds,
      pt,
      keepAspect ? false : ev.shiftKey,
      ev.altKey
    );
    const viewportEl =
      overlay.closest(".wb-board-viewport") ||
      overlay.parentElement?.closest(".wb-board-viewport") ||
      overlay;
    let view = visibleWorldBoundsFromViewport(
      (cx, cy) => host.stagePoint(cx, cy),
      viewportEl
    );
    if (view.w < 8 || view.h < 8) {
      view = { x: -1e9, y: -1e9, w: 2e9, h: 2e9 };
    }
    const map = (cx: number, cy: number) => host.stagePoint(cx, cy);
    let nextGroup: Bounds;
    if (keepAspect) {
      const scale = cornerScaleFromFreeBounds(resize.startBounds, freeGroup);
      nextGroup = boundsScaledFromHandle(
        resize.startBounds,
        resize.handle,
        scale,
        ev.altKey
      );
      resize.guides.renderAlignment();
    } else if (resize.rotationRad) {
      // Направляющие считаются по осям доски — для повёрнутого объекта они
      // показывали бы выравнивание, которого на экране нет.
      nextGroup = freeGroup;
      resize.guides.renderAlignment();
    } else {
      const snapped = snapResizeBounds(
        freeGroup,
        resize.handle,
        resize.candidateBounds,
        snapThresholdWorld(map, SMART_GUIDE_SNAP_PX),
        view
      );
      nextGroup = snapped.bounds;
      resize.guides.renderAlignment(snapped.guidesX, snapped.guidesY);
    }

    if (resize.rotationRad) {
      // Объект крутится вокруг своего центра, а центр при растягивании уезжает.
      // Сдвигаем рамку так, чтобы точка, за которую «держимся» (противоположный
      // угол или центр при Alt), осталась на месте на экране.
      const anchorStart = resizeAnchorPoint(resize.startBounds, resize.handle, ev.altKey);
      const [ax, ay] = rotatePoint(
        anchorStart.x,
        anchorStart.y,
        startCx,
        startCy,
        resize.rotationRad
      );
      const nextCx = nextGroup.x + nextGroup.w / 2;
      const nextCy = nextGroup.y + nextGroup.h / 2;
      const anchorNext = resizeAnchorPoint(nextGroup, resize.handle, ev.altKey);
      const [bx, by] = rotatePoint(
        anchorNext.x,
        anchorNext.y,
        nextCx,
        nextCy,
        resize.rotationRad
      );
      nextGroup = { ...nextGroup, x: nextGroup.x + (ax - bx), y: nextGroup.y + (ay - by) };
    }
    resize.guides.renderSpacing([]);
    const patches = scaleSnapshots(resize.startBounds, nextGroup, resize.snapshots);
    applyPatchesToDom(
      patches,
      resize.startBounds,
      nextGroup,
      resize.pathPointSnapshots,
      resize.transformSnapshots,
      resize.handle,
      ev.altKey,
      resize.snapshots
    );
    const selectionObjects = host
      .getSelection()
      .map((id) => host.findObject(id))
      .filter((o): o is SceneObject => Boolean(o));
    const displayBounds = unionBounds(selectionObjects) ?? nextGroup;
    applyGroupLayout(displayBounds, true, selectionRotationRad(selectionObjects));
  }

  function endResize(commit: boolean) {
    if (!resize) return;
    const state = resize;
    resize = null;
    document.removeEventListener("pointermove", onResizeMove);
    document.removeEventListener("pointerup", onResizeUp);
    document.removeEventListener("pointercancel", onResizeCancel);
    state.guides.destroy();
    setTransforming(false);
    if (!commit) {
      update();
      return;
    }
    commitTransformPatches(state.transformSnapshots, `resize-${Date.now()}`);
  }

  function onResizeUp() {
    endResize(true);
  }

  function onResizeCancel() {
    endResize(false);
  }

  function onRotateDown(ev: PointerEvent) {
    if (!host.canEdit) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();

    const ids = host.getSelection();
    const objects = ids
      .map((id) => host.findObject(id))
      .filter((o): o is SceneObject => Boolean(o && !o.locked));
    if (!selectionShowsRotateHandle(objects)) return;

    const bounds = unionBounds(objects);
    if (!bounds) return;
    const groupCenter = { x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 };
    const pt = host.stagePoint(ev.clientX, ev.clientY);

    const snapshots = new Map<string, ObjectTransformSnapshot>();
    for (const obj of objects) {
      snapshots.set(obj.id, captureObjectSnapshot(obj));
    }

    rotate = {
      groupCenter,
      startAngle: pointerAngleRad(groupCenter, pt),
      baseRotation: selectionRotationRad(objects),
      snapshots,
      moved: false,
    };

    rotateReadout.textContent = formatAngleDeg(rotate.baseRotation);
    rotateReadout.style.display = "";

    setTransforming(true);

    document.addEventListener("pointermove", onRotateMove);
    document.addEventListener("pointerup", onRotateUp);
    document.addEventListener("pointercancel", onRotateCancel);
  }

  function onRotateMove(ev: PointerEvent) {
    if (!rotate) return;
    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const angle = pointerAngleRad(rotate.groupCenter, pt);
    const delta = snapRotationDelta(
      angle - rotate.startAngle,
      rotate.baseRotation,
      ev.shiftKey
    );
    if (Math.abs(delta) > 0.001) rotate.moved = true;
    rotateReadout.textContent = formatAngleDeg(rotate.baseRotation + delta);

    const groupCenter = rotate.groupCenter;
    rotate.snapshots.forEach((snap, id) => {
      const obj = host.findObject(id);
      const el = host.getObjectElement(id);
      if (!obj || !el) return;
      applyGroupRotationDelta(obj, snap, groupCenter, delta);
      syncObjectDom(host, obj, el);
    });

    const ids = host.getSelection();
    const objects = ids.map((id) => host.findObject(id)).filter(Boolean) as SceneObject[];
    const bounds = unionBounds(objects);
    if (bounds) {
      applyGroupLayout(bounds, selectionShowsRotateHandle(objects), selectionRotationRad(objects));
    }
  }

  function endRotate(commit: boolean) {
    if (!rotate) return;
    const state = rotate;
    rotate = null;
    document.removeEventListener("pointermove", onRotateMove);
    document.removeEventListener("pointerup", onRotateUp);
    document.removeEventListener("pointercancel", onRotateCancel);
    rotateReadout.style.display = "none";
    setTransforming(false);
    if (!commit || !state.moved) {
      update();
      return;
    }
    commitTransformPatches(state.snapshots, `rotate-${Date.now()}`);
  }

  function onRotateUp() {
    endRotate(true);
  }

  function onRotateCancel() {
    endRotate(false);
  }

  handleEls.forEach((el) => {
    el.addEventListener("pointerdown", onHandleDown);
  });
  rotateHandle.addEventListener("pointerdown", onRotateDown);
  window.addEventListener("wb:live-viewport", onLiveViewport);

  return {
    update,
    destroy() {
      endResize(false);
      endRotate(false);
      window.removeEventListener("wb:live-viewport", onLiveViewport);
      handleEls.forEach((el) => {
        el.removeEventListener("pointerdown", onHandleDown);
      });
      rotateHandle.removeEventListener("pointerdown", onRotateDown);
      root.remove();
    },
  };
}
